import React, { useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useMount } from 'react-nb-hooks'
import values from 'lodash/values'
import includes from 'lodash/includes'
import clone from 'lodash/clone'
import objectQuery from 'loankeeper-common/containers/objectQuery'
import { useTwilight } from 'loankeeper-common/components/hocs/Twilight'
import { LookupType } from '@lk/api'

import { getLookupsByType, loadLookupsByTypeIfNeeded } from '../../../store/lookups'

export { LookupType }

const AllowedLookupTypes = values(LookupType)

export const useLookups = (preferedType, query = '* as lookups') => {
  const type = preferedType && includes(AllowedLookupTypes, preferedType) ? preferedType : LookupType.System

  const dispatch = useDispatch()

  const { startTwilight, endTwilight } = useTwilight()

  useMount(() => {
    async function loadLookups() {
      if (typeof startTwilight === 'function') {
        startTwilight()
      }

      await dispatch(loadLookupsByTypeIfNeeded(type))

      if (typeof endTwilight === 'function') {
        endTwilight()
      }
    }

    loadLookups()
  })

  // TODO: might be attached to global loading indicator (import from store/lookups)
  // const isLoading = useSelector(isAnyLookupLoading)
  const lookups = useSelector((state) => getLookupsByType(state, type))

  return useMemo(() => {
    if (!lookups) {
      return null
    }

    return objectQuery(clone(lookups), query)
  }, [lookups, query])
}

/**
 * HOC that fetches and then passes config to your component
 *
 * @param {Object} config
 * @param {string} [type='sys']
 * @param {ObjectQuery} [query='* as config'] @see ObjectQuery
 *
 * @returns {function(component: React.Component): React.Component}
 *
 * @example
 * withLookups()
 *
 *  withLookups({
 *      type: '',
 *  })
 *
 *  withLookups({
 *      query: '...'
 *  })
 *
 *  withLookups({
 *      type: LookupType.System,
 *      query: [
 *          'foo.bar',
 *          'foo.bar as foobar',
 *      ]
 *  })

 *  withLookups({
 *      type: LookupType.System,
 *      query: 'foo.bar as foobar',
 *  })
 */
export default (args) => (Component) => (props) => {
  const lookups = useLookups(args.type, args.query)

  if (!lookups) {
    return null
  }

  return <Component {...lookups} {...props} />
}
